import { buildChordString } from './chordBuilder.js';
import { getSectionNote } from './sectionMarkers.js';
/** The slots of a row in either shape. */
const rowSlots = (row) => 'bars' in row ? row.bars.flatMap(bar => bar.slots) : row.slots;
/**
 * Checks if a slot has content (a chord or lyrics is set)
 */
export const slotHasContent = (slot) => {
    const hasChord = slot.chord !== null;
    const hasLyrics = !!(slot.lyrics && slot.lyrics.trim().length > 0);
    return hasChord || hasLyrics;
};
/**
 * Checks if a row has any content (at least one slot with a chord)
 */
export const rowHasContent = (row) => {
    return rowSlots(row).some(slot => slotHasContent(slot));
};
/**
 * Checks if a section has any content (at least one row with content, or a
 * note — free text the user typed and would lose on delete)
 */
export const sectionHasContent = (section) => {
    return section.rows.some(row => rowHasContent(row)) || getSectionNote(section) !== null;
};
/**
 * Gets a human-readable description of what content would be lost
 */
export const getContentDescription = (section) => {
    const filledSlots = section.rows.reduce((total, row) => {
        return total + rowSlots(row).filter(slot => slotHasContent(slot)).length;
    }, 0);
    const totalRows = section.rows.length;
    if (filledSlots === 0) {
        return getSectionNote(section) !== null ? 'section note' : 'empty section';
    }
    const chordText = filledSlots === 1 ? 'chord' : 'chords';
    const rowText = totalRows === 1 ? 'row' : 'rows';
    return `${filledSlots} ${chordText} across ${totalRows} ${rowText}`;
};
/**
 * Gets a human-readable description of what content would be lost for a row
 */
export const getRowContentDescription = (row) => {
    const filledSlots = rowSlots(row).filter(slot => slotHasContent(slot)).length;
    if (filledSlots === 0) {
        return 'empty row';
    }
    const chordText = filledSlots === 1 ? 'chord' : 'chords';
    return `${filledSlots} ${chordText}`;
};
/**
 * Gets a human-readable description of what content would be lost for a slot
 * Returns chord name, "lyrics", "chord and lyrics", or "empty slot"
 */
export const getSlotContentDescription = (slot) => {
    const hasChord = slot.chord !== null;
    const hasLyrics = !!(slot.lyrics && slot.lyrics.trim().length > 0);
    if (hasChord && hasLyrics) {
        try {
            const chordName = buildChordString(slot.chord);
            return `${chordName} and lyrics`;
        }
        catch (error) {
            // Fallback if buildChordString fails
            return 'chord and lyrics';
        }
    }
    else if (hasChord) {
        try {
            return buildChordString(slot.chord);
        }
        catch (error) {
            // Fallback if buildChordString fails
            return 'chord';
        }
    }
    else if (hasLyrics) {
        return 'lyrics';
    }
    else {
        return 'empty slot';
    }
};
